import instance from "lib/axiosInterceptor";
import {
  UserSettings,
  FetchedApp,
  OAuthTokenResponse,
  Country,
  Provider,
  ConnectAppPayload,
  GetAppsFlipPayload,
  AppsFlipParams,
  ChangePasswordPayload,
  SetupPasswordPayload,
} from "interfaces";
import {
  SpeakerSettingsResponse,
  SpeakerSetupWithResumePayload,
} from "interfaces/interfaces-data";

/**
 * GET REQUESTS
 */

// Get user settings
export async function getUserSettingsService(): Promise<UserSettings> {
  const response = await instance.get<UserSettings>("/Settings", {
    suppressStatuses: [400],
    suppressMessages: ["User page settings not found"],
    returnEmptyOnSuppress: true, // NEW FLAG
  });
  return response.data;
}

// Get speaker settings
export async function getSpeakerSettingsService(): Promise<SpeakerSettingsResponse> {
  const response = await instance.get<SpeakerSettingsResponse>(
    "/Settings/Speaker",
    {
      suppressStatuses: [400],
      suppressMessages: ["Speaker not found"],
      returnEmptyOnSuppress: true,
    },
  );
  return response.data;
}

// Get display picture by username
export async function getDisplayPictureService(
  username: string,
): Promise<Blob> {
  const response = await instance.get(
    `/Settings/DisplayPicture?username=${username}`,
    {
      responseType: "blob",
      suppressStatuses: [400],
      suppressMessages: ["Display picture not found"],
      returnEmptyOnSuppress: true, // NEW FLAG
    },
  );
  return response.data;
}

// Get all apps
export async function getAppsService(): Promise<FetchedApp[]> {
  const response = await instance.get<FetchedApp[]>("/Apps");
  return response.data;
}

/**
 * POST REQUESTS
 */

// Connect an app
export async function connectAppService(
  data: ConnectAppPayload,
): Promise<string> {
  const response = await instance.post<string>("/Apps/Connect", data);
  return response.data;
}

// Change password
export async function changePasswordService(
  payload: ChangePasswordPayload,
): Promise<void> {
  const response = await instance.post<void>("/Account/ChangePassword", payload);
  return response.data;
}

// Setup password
export async function setupPasswordService(
  payload: SetupPasswordPayload,
): Promise<void> {
  const response = await instance.post<void>("/Account/setupPassword", payload);
  return response.data;
}

// Update username
export async function updateSettingsUsernameService(
  username: string,
): Promise<void> {
  const response = await instance.post<void>("/Settings/UpdateUsername", null, {
    params: { username },
  });
  return response.data;
}

// Disconnect an app
export async function disconnectAppService(app: number): Promise<void> {
  const response = await instance.post(`/Apps/Disconnect?app=${app}`);
  return response.data;
}
// Flip app ie enable and disable
export async function flipAppService(
  payload: GetAppsFlipPayload,
): Promise<void> {
  const { params } = payload;
  const queryParams: AppsFlipParams = {
    ...params,
  };

  // Remove undefined values
  Object.keys(queryParams).forEach((key) => {
    if (queryParams[key as keyof AppsFlipParams] === undefined) {
      delete queryParams[key as keyof AppsFlipParams];
    }
  });

  const response = await instance.post(`/Apps/flip`, null, {
    params: queryParams,
  });
  return response.data;
}

// Decode OAuth token
export async function decodeOAuthTokenService(
  code: string,
): Promise<OAuthTokenResponse> {
  const response = await instance.post<OAuthTokenResponse>(
    `/Account/OAuth/Token`,
    { code, decoded: false },
  );
  return response.data;
}

// Get Flutterwave countries
export async function getFlutterwaveCountriesService(): Promise<Country[]> {
  const response = await instance.get<Country[]>("/Countries/Flutterwave");
  return response.data;
}

// Get Stripe countries
export async function getStripeCountriesService(): Promise<Country[]> {
  const response = await instance.get<Country[]>("/Countries/Stripe");
  return response.data;
}

// Get Flutterwave banks
export async function getFlutterwaveBanksService(
  country: string,
): Promise<Provider[]> {
  const response = await instance.get<Provider[]>(
    `/Banks/Flutterwave?country=${country}`,
  );
  return response.data;
}

// Prefill speaker profile with uploaded resume
export async function setupSpeakerWithResumeService(
  payload: SpeakerSetupWithResumePayload,
): Promise<SpeakerSettingsResponse> {
  const formData = new FormData();
  formData.append("resume", payload.resume);

  const response = await instance.post<SpeakerSettingsResponse>(
    "/Settings/Speaker/SetupWithResume",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return response.data;
}
